import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, post } from "@/lib/api";
import { EMAIL_RE } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { FieldError } from "./States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ChangeEmailDialog = ({ open, onOpenChange }) => {
  const { profile, refreshProfile } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setF({ email: "", password: "" });
      setErrors({});
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const email = f.email.trim().toLowerCase();
    const errs = {};
    if (!EMAIL_RE.test(email)) errs.email = "Enter a valid email address";
    else if (email === profile.email) errs.email = "This is already your email";
    if (!f.password) errs.password = "Enter your current password";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await post("/auth/change-email", { new_email: email, current_password: f.password });
      await refreshProfile();
      toast.success(`Email changed. Sign in with ${email} from now on.`);
      onOpenChange(false);
    } catch (err) {
      setErrors({ form: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, props) => (
    <div>
      <Label htmlFor={`ce-${key}`}>{label}</Label>
      <Input id={`ce-${key}`} value={f[key]} onChange={(e) => setF({ ...f, [key]: e.target.value })} className="mt-1.5" data-testid={`change-email-${key}-input`} {...props} />
      <FieldError message={errors[key]} testId={`change-email-${key}-error`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md" data-testid="change-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Change your email</DialogTitle>
          <DialogDescription>You&apos;ll sign in with the new email straight away. We&apos;ll let both addresses know.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="space-y-4">
          <p className="text-sm text-slate-600">Current: <span className="font-medium text-slate-900">{profile.email}</span></p>
          {field("email", "New email", { type: "email", autoComplete: "email" })}
          {field("password", "Current password", { type: "password", autoComplete: "current-password" })}
          {errors.form && <p role="alert" className="text-sm text-rose-600" data-testid="change-email-form-error">{errors.form}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="change-email-cancel-button">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="change-email-submit-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Change email
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
